import React from "react";
import { useTranslation } from "react-i18next";
import AllContacts from "../AllContacts";
import { Link } from "react-router-dom";

export default function Home() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-vinefin-900 via-vinefin-700 to-vinefin-500 text-white py-20 px-6 md:px-12 overflow-hidden rounded-b-[3rem] shadow-xl mb-12">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight drop-shadow-md">
            {t("home.title")}
          </h1>
          <p className="text-lg md:text-xl text-vinefin-100 mb-10 max-w-2xl mx-auto leading-relaxed">
            {t("home.aboutus")}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/projects"
              className="bg-white text-vinefin-700 px-8 py-3 rounded-full font-bold hover:bg-vinefin-50 hover:scale-105 transition transform shadow-lg"
            >
              {t("navbar.projects")}
            </Link>
            <button
              onClick={() =>
                window.scrollTo({
                  top: document.documentElement.scrollHeight,
                  behavior: "smooth",
                })
              }
              className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-full font-bold hover:bg-white/10 hover:scale-105 transition transform"
            >
              {t("navbar.contact")}
            </button>
          </div>
        </div>

        {/* Abstract Background Shapes */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-30 pointer-events-none">
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-vinefin-300 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
          <div className="absolute top-0 -right-4 w-96 h-96 bg-vinefin-400 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-96 h-96 bg-vinefin-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
        </div>
      </div>

      {/* Features / Content Section */}
      <div className="py-12 px-6 md:px-12">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center mb-24">
          <div className="order-2 md:order-1">
            <h2 className="text-3xl font-bold text-vinefin-900 mb-6 relative inline-block">
              {t("home.title2")}
              <span className="absolute bottom-0 left-0 w-full h-2 bg-vinefin-200 opacity-50 -z-10"></span>
            </h2>
            <p className="text-gray-600 leading-relaxed text-lg">
              {t("home.inworks")}
            </p>
          </div>
          <div className="order-1 md:order-2 bg-gradient-to-br from-vinefin-50 to-white rounded-3xl p-8 h-64 flex items-center justify-center shadow-inner border border-vinefin-100">
            <svg viewBox="0 0 200 200" className="w-full h-full">
              {/* Elderly care home building */}
              <rect
                x="50"
                y="80"
                width="100"
                height="80"
                fill="#7A1C7A"
                rx="5"
              />
              <rect
                x="55"
                y="85"
                width="20"
                height="25"
                fill="#d15cd1"
                rx="2"
              />
              <rect
                x="80"
                y="85"
                width="20"
                height="25"
                fill="#d15cd1"
                rx="2"
              />
              <rect
                x="105"
                y="85"
                width="20"
                height="25"
                fill="#d15cd1"
                rx="2"
              />
              <rect
                x="130"
                y="85"
                width="20"
                height="25"
                fill="#d15cd1"
                rx="2"
              />
              <rect
                x="70"
                y="120"
                width="30"
                height="40"
                fill="#f0b8f0"
                rx="2"
              />
              <path d="M50 80 L100 50 L150 80" fill="#631763" />
              {/* Heart symbol representing care */}
              <path
                d="M100 140 C100 140 115 125 115 115 C115 110 110 105 105 105 C102 105 100 107 100 107 C100 107 98 105 95 105 C90 105 85 110 85 115 C85 125 100 140 100 140 Z"
                fill="#e68ae6"
              />
              {/* People silhouettes */}
              <circle cx="35" cy="130" r="8" fill="#b03cb0" />
              <rect
                x="28"
                y="138"
                width="14"
                height="18"
                fill="#b03cb0"
                rx="7"
              />
              <circle cx="165" cy="130" r="8" fill="#b03cb0" />
              <rect
                x="158"
                y="138"
                width="14"
                height="18"
                fill="#b03cb0"
                rx="7"
              />
              {/* Ground */}
              <rect
                x="20"
                y="160"
                width="160"
                height="5"
                fill="#f5d0f5"
                rx="2"
              />
            </svg>
          </div>
        </div>

        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center mb-12">
          <div className="bg-gradient-to-br from-vinefin-50 to-white rounded-3xl p-8 h-64 flex items-center justify-center shadow-inner border border-vinefin-100">
            <svg viewBox="0 0 200 200" className="w-full h-full">
              {/* Sun representing solar energy */}
              <circle cx="50" cy="50" r="20" fill="#e68ae6" />
              <line
                x1="50"
                y1="20"
                x2="50"
                y2="10"
                stroke="#d15cd1"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <line
                x1="70"
                y1="30"
                x2="77"
                y2="23"
                stroke="#d15cd1"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <line
                x1="80"
                y1="50"
                x2="90"
                y2="50"
                stroke="#d15cd1"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <line
                x1="70"
                y1="70"
                x2="77"
                y2="77"
                stroke="#d15cd1"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <line
                x1="30"
                y1="30"
                x2="23"
                y2="23"
                stroke="#d15cd1"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <line
                x1="20"
                y1="50"
                x2="10"
                y2="50"
                stroke="#d15cd1"
                strokeWidth="3"
                strokeLinecap="round"
              />
              <line
                x1="30"
                y1="70"
                x2="23"
                y2="77"
                stroke="#d15cd1"
                strokeWidth="3"
                strokeLinecap="round"
              />

              {/* Solar panels */}
              <g transform="translate(90, 80)">
                <rect
                  x="0"
                  y="0"
                  width="80"
                  height="45"
                  fill="#7A1C7A"
                  rx="2"
                />
                <line
                  x1="0"
                  y1="15"
                  x2="80"
                  y2="15"
                  stroke="#f0b8f0"
                  strokeWidth="1"
                />
                <line
                  x1="0"
                  y1="30"
                  x2="80"
                  y2="30"
                  stroke="#f0b8f0"
                  strokeWidth="1"
                />
                <line
                  x1="20"
                  y1="0"
                  x2="20"
                  y2="45"
                  stroke="#f0b8f0"
                  strokeWidth="1"
                />
                <line
                  x1="40"
                  y1="0"
                  x2="40"
                  y2="45"
                  stroke="#f0b8f0"
                  strokeWidth="1"
                />
                <line
                  x1="60"
                  y1="0"
                  x2="60"
                  y2="45"
                  stroke="#f0b8f0"
                  strokeWidth="1"
                />
              </g>

              {/* Wind turbine */}
              <rect
                x="30"
                y="120"
                width="4"
                height="50"
                fill="#631763"
                rx="2"
              />
              <circle cx="32" cy="118" r="8" fill="#b03cb0" />
              <ellipse
                cx="32"
                cy="95"
                rx="3"
                ry="23"
                fill="#d15cd1"
                opacity="0.8"
              />
              <ellipse
                cx="32"
                cy="95"
                rx="3"
                ry="23"
                fill="#d15cd1"
                opacity="0.8"
                transform="rotate(60 32 118)"
              />
              <ellipse
                cx="32"
                cy="95"
                rx="3"
                ry="23"
                fill="#d15cd1"
                opacity="0.8"
                transform="rotate(120 32 118)"
              />

              {/* Energy waves */}
              <path
                d="M 110 140 Q 120 135 130 140"
                stroke="#e68ae6"
                strokeWidth="2"
                fill="none"
              />
              <path
                d="M 110 150 Q 125 143 140 150"
                stroke="#e68ae6"
                strokeWidth="2"
                fill="none"
              />
              <path
                d="M 115 160 Q 130 152 145 160"
                stroke="#e68ae6"
                strokeWidth="2"
                fill="none"
              />

              {/* Ground */}
              <rect
                x="10"
                y="170"
                width="180"
                height="5"
                fill="#f5d0f5"
                rx="2"
              />
            </svg>
          </div>
          <div>
            <h2 className="text-3xl font-bold text-vinefin-900 mb-6 relative inline-block">
              {t("home.title3")}
              <span className="absolute bottom-0 left-0 w-full h-2 bg-vinefin-200 opacity-50 -z-10"></span>
            </h2>
            <p className="text-gray-600 leading-relaxed text-lg">
              {t("home.ourgoals")}
            </p>
          </div>
        </div>
      </div>

      <AllContacts />
    </div>
  );
}
